import React from 'react';
import { MembershipType } from '../../types/member';
import type { InternationalCommittee } from '../../types/committee';
import FormField from '../common/FormField';
import CommitteeSelector from './CommitteeSelector';
import { FormRow, CheckboxContainer, Checkbox, CheckboxLabel } from '../../design-system/patterns/FormLayout';

export interface MembershipSectionProps {
  formData: {
    membershipType: MembershipType;
    mainCommittee: string;
    additionalCommittees: string[];
    isHonoraryMember?: boolean;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  committees: InternationalCommittee[];
  t: (key: string) => string;
  mainTitle?: string;
  mainDescription?: string;
  secondaryTitle?: string;
  secondaryDescription?: string;
  showMembershipType?: boolean;
  showHonoraryCheckbox?: boolean;
  maxAdditionalCommittees?: number;
  /** Clear committee validation errors when the user changes selections. */
  onCommitteeChange?: () => void;
}

export const MembershipSection: React.FC<MembershipSectionProps> = ({
  formData,
  setFormData,
  committees,
  t,
  mainTitle,
  mainDescription = '',
  secondaryTitle,
  secondaryDescription = '',
  showMembershipType = true,
  showHonoraryCheckbox = false,
  maxAdditionalCommittees = 3,
  onCommitteeChange,
}) => {
  return (
    <>
      {showMembershipType && (
        <FormRow>
          <FormField
            label={t('profile.membershipType')}
            select
            options={[
              { value: MembershipType.Regular.toString(), label: t('membership.regular') },
              { value: MembershipType.Student.toString(), label: t('membership.student') },
              { value: MembershipType.Retired.toString(), label: t('membership.retired') },
            ]}
            value={formData.membershipType.toString()}
            onChange={(e) =>
              setFormData((prev: any) => ({
                ...prev,
                membershipType: parseInt(e.target.value, 10) as MembershipType,
              }))
            }
          />
        </FormRow>
      )}
      <CommitteeSelector
        committees={committees}
        mainCommittee={formData.mainCommittee}
        secondaryCommittees={formData.additionalCommittees}
        onMainCommitteeChange={(committeeId) => {
          setFormData((prev: any) => ({ ...prev, mainCommittee: committeeId }));
          onCommitteeChange?.();
        }}
        onSecondaryCommitteesChange={(committeeIds) => {
          const limitedIds = committeeIds.slice(0, maxAdditionalCommittees);
          setFormData((prev: any) => ({ ...prev, additionalCommittees: limitedIds }));
          onCommitteeChange?.();
        }}
        mainTitle={mainTitle ?? t('register.individual.mainCommitteeVoting')}
        mainDescription={mainDescription}
        secondaryTitle={secondaryTitle ?? t('register.individual.committeesWithoutVoting')}
        secondaryDescription={secondaryDescription}
      />
      {showHonoraryCheckbox && (
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="isHonoraryMember"
            checked={formData.isHonoraryMember ?? false}
            onChange={(e) =>
              setFormData((prev: any) => ({ ...prev, isHonoraryMember: e.target.checked }))
            }
          />
          <CheckboxLabel htmlFor="isHonoraryMember">{t('profile.isHonoraryMember')}</CheckboxLabel>
        </CheckboxContainer>
      )}
    </>
  );
};

export default MembershipSection;
